/**
 * @module @verba/chat-sdk/ui
 * CSS-in-JS helpers for creating and managing the widget's DOM elements.
 * All styles are scoped to avoid conflicts with host application CSS.
 */

import type { BubblePosition, ChatBubbleConfig } from './types.ts'

// ─── Constants ───────────────────────────────────────────────────────────────

const STYLE_ID = 'verba-chat-sdk-styles'
const CONTAINER_CLASS = 'verba-widget-container'
const IFRAME_CLASS = 'verba-widget-iframe'
const BUBBLE_CLASS = 'verba-widget-bubble'
const BUBBLE_OPEN_CLASS = 'verba-widget-bubble--open'
const BODY_SCROLL_LOCK_CLASS = 'verba-body-scroll-lock'
const POPUP_CLASS = 'verba-widget-popup'
const POPUP_DISMISS_BTN_CLASS = 'verba-widget-popup-dismiss'
const BADGE_CLASS = 'verba-widget-badge'

/** CSS class toggled on the floating container to switch it into fullscreen mode on mobile. */
export const MOBILE_FULLSCREEN_CLASS = 'verba-widget--mobile-fullscreen'

/** CSS class toggled on the floating container to enlarge the iframe panel (desktop only). */
export const EXPANDED_CLASS = 'verba-widget--expanded'

/** Viewport width (px) at/below which the floating widget goes fullscreen instead of popping up. */
export const MOBILE_BREAKPOINT = 768

import { animate } from 'framer-motion/dom'

// ─── Style Injection ─────────────────────────────────────────────────────────

/**
 * Injects a `<style>` tag into the document `<head>` with all widget styles.
 * Idempotent — safe to call multiple times.
 */
export function injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return

    const css = `
    /* ── Verba Chat SDK ── */

    .${CONTAINER_CLASS} {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 2147483600;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 12px;
      font-family: system-ui, -apple-system, sans-serif;
    }

    .${CONTAINER_CLASS}[data-position="bottom-left"] {
      right: unset;
      left: 24px;
      align-items: flex-start;
    }

    .${CONTAINER_CLASS}.verba-widget--inline {
      position: static;
      bottom: unset;
      right: unset;
      left: unset;
      width: 100%;
      height: 100%;
      display: block;
    }

    .${IFRAME_CLASS} {
      border: none;
      border-radius: 16px;
      width: 400px;
      height: 77vh;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.2);
      opacity: 0;
      display: none;
      transform: translateY(12px) scale(0.97);
      transform-origin: bottom right;
      pointer-events: none;
      background: white;
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* ── Expanded (desktop floating widget only; inline and mobile-fullscreen override via !important below) ── */
    .${CONTAINER_CLASS}.${EXPANDED_CLASS} .${IFRAME_CLASS} {
      width: min(720px, calc(100vw - 40px));
    }

    .${CONTAINER_CLASS}[data-position="bottom-left"] .${IFRAME_CLASS} {
      transform-origin: bottom left;
    }

    .${CONTAINER_CLASS}.verba-widget--inline .${IFRAME_CLASS} {
      display: block !important;
      width: 100% !important;
      height: 100%;
      border-radius: 0;
      box-shadow: none;
      opacity: 1 !important;
      transform: none !important;
      pointer-events: all !important;
    }

    /* ── Body scroll lock (applied while the mobile-fullscreen widget is open) ── */
    body.${BODY_SCROLL_LOCK_CLASS} {
      overflow: hidden !important;
    }

    /* ── Bubble Button ── */

    .verba-widget-bubble-wrapper {
      position: relative;
      display: inline-flex;
      flex-shrink: 0;
    }

    .${BUBBLE_CLASS} {
      border-radius: 50%;
      border: none;
      cursor: pointer;
      box-shadow: 0 4px 20px var(--verba-shadow-color, rgba(0,0,0,0.15)), 0 2px 6px var(--verba-shadow-color, rgba(0,0,0,0.15));
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
                  box-shadow 0.22s ease;
      flex-shrink: 0;
      outline: none;
      overflow: hidden;
    }

    .${BADGE_CLASS} {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #ef4444;
      border: 2px solid #ffffff;
      opacity: 0;
      transform: scale(0);
      pointer-events: none;
      z-index: 1;
    }

    .${BUBBLE_CLASS}:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 28px var(--verba-shadow-color, rgba(0,0,0,0.3)), 0 2px 10px var(--verba-shadow-color, rgba(0,0,0,0.3));
    }

    .${BUBBLE_CLASS}:active {
      transform: scale(0.95);
    }

    .${BUBBLE_CLASS} .verba-bubble-icon {
      position: absolute;
    }

    .${BUBBLE_CLASS} .verba-bubble-icon--chat { opacity: 1; transform: scale(1) rotate(0deg); }
    .${BUBBLE_CLASS} .verba-bubble-icon--close { opacity: 0; transform: scale(0.5) rotate(-90deg); }

    /* ── Greeting / notification popup ── */

    .${POPUP_CLASS} {
      position: absolute;
      bottom: calc(100% + 12px);
      right: 0;
      width: max-content;
      max-width: min(320px, calc(100vw - 48px));
      background: #ffffff;
      color: #111827;
      border-radius: 14px;
      padding: 14px 34px 14px 16px;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15);
      font-size: 14px;
      line-height: 1.45;
      opacity: 0;
      transform: translateY(8px) scale(0.96);
      pointer-events: none;
      cursor: pointer;
      z-index: 2147483601;
    }

    .${CONTAINER_CLASS}[data-position="bottom-left"] .${POPUP_CLASS} {
      right: unset;
      left: 0;
    }

    .${POPUP_CLASS} p {
      margin: 0;
    }

    .${POPUP_DISMISS_BTN_CLASS} {
      position: absolute;
      top: 2px;
      right: 8px;
      width: 22px;
      height: 22px;
      border: none;
      border-radius: 50%;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      transition: background 0.2s ease;
    }

    .${POPUP_DISMISS_BTN_CLASS}:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    .${CONTAINER_CLASS}.${MOBILE_FULLSCREEN_CLASS} .${POPUP_CLASS} {
      display: none;
    }

    /* ── Mobile (idle bubble corner spacing on small phones) ── */
    @media (max-width: 480px) {
      .${CONTAINER_CLASS} {
        right: 12px;
        bottom: 12px;
      }

      .${CONTAINER_CLASS}[data-position="bottom-left"] {
        left: 12px;
        right: unset;
      }
    }

    /* ── Mobile fullscreen (floating widget only; inline is unaffected) ── */
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      .${CONTAINER_CLASS}.${MOBILE_FULLSCREEN_CLASS} {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100dvh;
        flex-direction: column;
        align-items: stretch;
        gap: 0;
      }

      .${CONTAINER_CLASS}.${MOBILE_FULLSCREEN_CLASS} .${IFRAME_CLASS} {
        flex: 1 1 auto;
        width: 100% !important;
        height: 100% !important;
        max-height: none;
        border-radius: 0;
        box-shadow: none;
      }

      .${CONTAINER_CLASS}.${MOBILE_FULLSCREEN_CLASS} .${BUBBLE_CLASS} {
        display: none;
      }
    }
  `

    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = css
    document.head.appendChild(style)
}

/** Removes the injected `<style>` tag from the document. */
export function removeInjectedStyles(): void {
    document.getElementById(STYLE_ID)?.remove()
}

// ─── Element Factories ───────────────────────────────────────────────────────

/**
 * Creates the fixed-position outer container div used in floating mode.
 */
export function createFloatingContainer(position: BubblePosition): HTMLDivElement {
    const div = document.createElement('div')
    div.className = CONTAINER_CLASS
    div.dataset.position = position
    div.setAttribute('role', 'complementary')
    return div
}

/**
 * Creates an inline container — wrapper inside a user-supplied element.
 */
export function createInlineContainer(): HTMLDivElement {
    const div = document.createElement('div')
    div.className = `${CONTAINER_CLASS} verba-widget--inline`
    div.style.width = '100%'
    div.style.height = '100%'
    return div
}

/**
 * Creates the `<iframe>` element with correct sandbox + security attributes.
 */
export function createIframeElement(src: string): HTMLIFrameElement {
    const iframe = document.createElement('iframe')
    iframe.className = IFRAME_CLASS
    iframe.src = src
    iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-popups allow-forms allow-downloads'
    )
    iframe.setAttribute('allow', 'clipboard-write')
    // `eager`, not `lazy`: the iframe is `display: none` while the chat is
    // closed (see `.verba-widget-iframe` below), and Chrome never lazy-loads an
    // element with no layout box — that would silently defer the whole
    // INIT_WIDGET/READY handshake (session creation, config fetch, BUBBLE_CONFIG)
    // until the visitor's first click, which is not how `init()` is meant to work.
    iframe.setAttribute('loading', 'eager')
    iframe.setAttribute('title', 'Verba AI Chat Widget')
    iframe.setAttribute('aria-label', 'Chat support widget')
    return iframe
}

/**
 * Creates the floating bubble toggle button with customizable style params.
 */
export function createBubbleButton(config?: ChatBubbleConfig): {
    wrapper: HTMLDivElement
    button: HTMLButtonElement
    badge: HTMLDivElement
} {
    const button = document.createElement('button')
    button.className = `${BUBBLE_CLASS} verba-bubble--pulse`
    button.setAttribute('aria-label', 'Open chat')
    button.setAttribute('aria-expanded', 'false')
    button.setAttribute('type', 'button')

    const size = config?.size ?? 56
    button.style.width = `${size}px`
    button.style.height = `${size}px`
    button.style.background = config?.color ?? '#ffffff'

    // Chat icon wrapper
    const chatIcon = document.createElement('span')
    chatIcon.className = 'verba-bubble-icon verba-bubble-icon--chat'
    chatIcon.style.display = 'flex'
    chatIcon.style.alignItems = 'center'
    chatIcon.style.justifyContent = 'center'

    if (config?.icon) {
        chatIcon.innerHTML = `<img width="26" height="26" src="${config.icon}" alt="Chat icon" />`
    } else {
        chatIcon.innerHTML = `
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="${config?.chatIconStrokeColor ?? '#000000'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    `
    }

    // Close (X) icon
    const closeIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    closeIcon.setAttribute('class', 'verba-bubble-icon verba-bubble-icon--close')
    closeIcon.setAttribute('width', '22')
    closeIcon.setAttribute('height', '22')
    closeIcon.setAttribute('viewBox', '0 0 24 24')
    closeIcon.setAttribute('fill', 'none')
    closeIcon.setAttribute('stroke', config?.closeIconColor ?? '#000000')
    closeIcon.setAttribute('stroke-width', '2.5')
    closeIcon.setAttribute('stroke-linecap', 'round')
    closeIcon.innerHTML = `
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  `
    button.appendChild(chatIcon)
    button.appendChild(closeIcon)

    if (config?.shadowColor) {
        button.style.setProperty('--verba-shadow-color', config.shadowColor)
    }

    const badge = document.createElement('div')
    badge.className = BADGE_CLASS
    badge.style.background = config?.badgeColor ?? '#ef4444'
    badge.setAttribute('aria-hidden', 'true')

    const wrapper = document.createElement('div')
    wrapper.className = 'verba-widget-bubble-wrapper'
    wrapper.appendChild(button)
    wrapper.appendChild(badge)

    return { wrapper, button, badge }
}

/**
 * Creates the greeting / notification popup shell (text + dismiss button).
 * The caller sets `textEl.textContent` for whichever message it wants shown.
 */
export function createGreetingPopup(): {
    popup: HTMLDivElement
    dismissButton: HTMLButtonElement
    textEl: HTMLParagraphElement
} {
    const popup = document.createElement('div')
    popup.className = POPUP_CLASS
    popup.setAttribute('role', 'status')

    const textEl = document.createElement('p')
    popup.appendChild(textEl)

    const dismissButton = document.createElement('button')
    dismissButton.className = POPUP_DISMISS_BTN_CLASS
    dismissButton.type = 'button'
    dismissButton.setAttribute('aria-label', 'Dismiss')
    dismissButton.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5" stroke-linecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  `
    popup.appendChild(dismissButton)

    return { popup, dismissButton, textEl }
}

/** Truncates a message to `maxLength` characters, appending an ellipsis if cut. */
export function truncateMessage(text: string, maxLength = 140): string {
    const trimmed = text.trim()
    if (trimmed.length <= maxLength) return trimmed
    return `${trimmed.slice(0, maxLength).trimEnd()}…`
}

// ─── Mobile Fullscreen Helpers ───────────────────────────────────────────────

/** True when the viewport is at/below `MOBILE_BREAKPOINT`. */
export function isMobileViewport(): boolean {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
}

/** Prevents the host page from scrolling behind a fullscreen mobile widget. */
export function lockBodyScroll(): void {
    document.body.classList.add(BODY_SCROLL_LOCK_CLASS)
}

/** Restores host page scrolling. */
export function unlockBodyScroll(): void {
    document.body.classList.remove(BODY_SCROLL_LOCK_CLASS)
}

// ─── State Helpers ───────────────────────────────────────────────────────────

/** Animate the iframe into view. */
export function showIframe(iframe: HTMLIFrameElement): void {
    iframe.style.pointerEvents = 'all'
    animate(
        iframe,
        { opacity: 1, display: 'block', y: 0, scale: 1 },
        { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
    )
}

/** Animate the iframe out of view (hides it). */
export function hideIframe(iframe: HTMLIFrameElement): void {
    iframe.style.pointerEvents = 'none'
    animate(
        iframe,
        { opacity: 0, display: 'none', y: 12, scale: 0.97 },
        { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
    )
}

/** Animate the greeting/notification popup into view. */
export function showPopup(popup: HTMLElement): void {
    popup.style.pointerEvents = 'all'
    animate(popup, { opacity: 1, y: 0, scale: 1 }, { duration: 0.25, ease: [0.4, 0, 0.2, 1] })
}

/** Animate the greeting/notification popup out of view. */
export function hidePopup(popup: HTMLElement): void {
    popup.style.pointerEvents = 'none'
    animate(popup, { opacity: 0, y: 8, scale: 0.96 }, { duration: 0.2, ease: [0.4, 0, 0.2, 1] })
}

/** Animate the unread-message badge into view. */
export function showBadge(badge: HTMLElement): void {
    animate(badge, { opacity: 1, scale: 1 }, { duration: 0.2, ease: [0.34, 1.56, 0.64, 1] })
}

/** Animate the unread-message badge out of view. */
export function hideBadge(badge: HTMLElement): void {
    animate(badge, { opacity: 0, scale: 0 }, { duration: 0.15, ease: [0.4, 0, 0.2, 1] })
}

/** Toggle bubble button's open/close icon state. */
export function setBubbleOpen(button: HTMLButtonElement, isOpen: boolean): void {
    button.classList.toggle(BUBBLE_OPEN_CLASS, isOpen)
    button.setAttribute('aria-label', isOpen ? 'Close chat' : 'Open chat')
    button.setAttribute('aria-expanded', String(isOpen))

    const chatIcon = button.querySelector('.verba-bubble-icon--chat') as SVGElement
    const closeIcon = button.querySelector('.verba-bubble-icon--close') as SVGElement

    if (isOpen) {
        animate(
            chatIcon,
            { opacity: 0, scale: 0.5, rotate: 90 },
            { duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }
        )
        animate(
            closeIcon,
            { opacity: 1, scale: 1, rotate: 0 },
            { duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }
        )
    } else {
        animate(
            chatIcon,
            { opacity: 1, scale: 1, rotate: 0 },
            { duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }
        )
        animate(
            closeIcon,
            { opacity: 0, scale: 0.5, rotate: -90 },
            { duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }
        )
    }
}
